import { releaseKey } from '../lib/release';
import { releaseAudioLanguages, releaseOptions } from '../lib/release-options';
import { isImmediatePlayerRelease } from '../lib/playback';
import { useI18n } from '../lib/use-i18n';
import type { DiscoveryMovie, MovieSummary, Release } from '../lib/types';
import { MovieCard } from './MovieCard';

export function SearchPanel({ query, libraryResults, metadataResults, loading, onOpen }: {
  query: string;
  libraryResults: MovieSummary[];
  metadataResults: DiscoveryMovie[];
  loading: boolean;
  onOpen: (movie: MovieSummary | DiscoveryMovie) => void;
}) {
  const { t } = useI18n();
  return (
    <section className="search-view">
      {!query.trim() && <div className="empty-state search-empty"><b>{t('searchMovies')}</b><span>{t('startTyping')}</span></div>}
      {loading && <div className="loading-line" />}
      {query.trim() && !loading && !libraryResults.length && !metadataResults.length && <div className="empty-state"><b>{t('noResults')}</b><span>{t('tryAnotherTitle')}</span></div>}
      {libraryResults.length > 0 && <ResultGrid title={t('inYourLibrary')} items={libraryResults} onOpen={onOpen} />}
      {metadataResults.length > 0 && (
        <section className="search-results-section">
          <div className="section-heading"><div><h2>{t('availableToAdd')}</h2></div></div>
          <div className="search-result-grid">
            {metadataResults.map((movie, index) => <MovieCard key={movie.tmdbId} movie={movie} index={index} compact onOpen={onOpen} />)}
          </div>
        </section>
      )}
    </section>
  );
}

function ResultGrid({ title, items, onOpen }: { title: string; items: MovieSummary[]; onOpen: (movie: MovieSummary) => void }) {
  const { t } = useI18n();
  return (
    <section className="search-results-section">
      <div className="section-heading"><div><span>{t('live')}</span><h2>{title}</h2></div></div>
      <div className="search-result-grid">{items.map((movie, index) => <MovieCard key={movie.id} movie={movie} index={index} compact onOpen={(item) => onOpen(item as MovieSummary)} />)}</div>
    </section>
  );
}

export function ReleasePicker({ movie, releases, loading, acquiringKey, acquireProgress, onClose, onAcquire }: {
  movie: DiscoveryMovie;
  releases: Release[];
  loading: boolean;
  acquiringKey: string | null;
  acquireProgress: number | null;
  onClose: () => void;
  onAcquire: (release: Release) => void;
}) {
  const { localeTag, t } = useI18n();
  const options = releaseOptions(releases);
  const activeRelease = acquiringKey
    ? options.find((release) => releaseKey(release) === acquiringKey) ?? null
    : null;
  const launchStyle = movie.backdrop ? { backgroundImage: `url("${movie.backdrop}")` } : undefined;
  return (
    <div className="release-overlay" role="dialog" aria-modal="true">
      <div className={`release-panel glass-panel ${activeRelease ? 'release-panel-launching' : ''}`}>
        {activeRelease ? (
          <div className="release-launch" role="status" aria-live="polite" aria-label={t('preparingStream')}>
            <div className="release-launch-backdrop" style={launchStyle} />
            <div className="release-launch-shade" />
            <button className="release-launch-close" data-focusable="true" type="button" onClick={onClose} aria-label={t('close')}>×</button>
            <div className="release-launch-visual" aria-hidden="true">
              <i /><i /><i /><span>▶</span>
            </div>
            <div className="release-launch-copy">
              <small>{movie.year ? `${movie.year} · ` : ''}{activeRelease.quality || t('qualityUnknown')}</small>
              <h2>{movie.title}</h2>
              <p>{isImmediatePlayerRelease(activeRelease) ? t('preparingStream') : t('adding')}</p>
              <div className="release-launch-progress" aria-hidden="true"><i style={acquireProgress && acquireProgress > 0 ? { width: `${acquireProgress}%` } : undefined} /></div>
            </div>
          </div>
        ) : <>
        <header><div><h2>{t('chooseVersion')} <small>{movie.title}{movie.year ? ` · ${movie.year}` : ''}</small></h2></div><button data-focusable="true" type="button" onClick={onClose} aria-label={t('close')}>×</button></header>
        {loading && !releases.length ? <div className="release-loading" role="status" aria-live="polite">
          <div className="release-search-visual" aria-hidden="true"><i /><i /><i /><span>▶</span></div>
          <b>{t('findingVersions')}</b><span>{t('findingHint')}</span>
          <div className="release-search-skeleton" aria-hidden="true"><i /><i /><i /></div>
        </div> : !releases.length ? <div className="empty-state"><b>{t('titleUnavailable')}</b><span>{t('tryLater')}</span></div> : (
          <div className="release-list">{options.map((release, index) => {
            const audioLanguages = releaseAudioLanguages(release);
            const audio = audioLanguages.length
              ? audioLanguages.map((code) => languageName(code, localeTag, t('multipleLanguages'))).join(' · ')
              : t('original');
            return (
            <button key={`${releaseKey(release)}-${index}`} data-focusable="true" data-autofocus={index === 0 ? 'true' : undefined} onClick={() => onAcquire(release)} disabled={Boolean(acquiringKey)} aria-busy={acquiringKey === releaseKey(release)} aria-label={`${release.quality || t('qualityUnknown')}, ${audio}`}>
              <span className="release-main">
                <span className="release-badges">
                  <b>{release.quality || t('qualityUnknown')}</b>
                  {release.hdr && <em>HDR</em>}
                  <em className={isImmediatePlayerRelease(release) ? 'ready' : ''}>{isImmediatePlayerRelease(release) ? t('onTheFly') : t('preparationRequired')}</em>
                </span>
                <small className="release-languages"><span>{t('audio')}</span>{audio}</small>
              </span>
              <span className="release-action">{acquiringKey === releaseKey(release) ? (isImmediatePlayerRelease(release) ? t('startingPlayback') : t('adding')) : (isImmediatePlayerRelease(release) ? t('watch') : t('prepare'))}</span>
            </button>
          );})}</div>
        )}
        </>}
      </div>
    </div>
  );
}

function languageName(code: string, localeTag: string, multipleLanguages: string): string {
  const normalized: Record<string, string> = {
    eng:'en', rus:'ru', ger:'de', deu:'de', fra:'fr', fre:'fr', spa:'es', ita:'it', pol:'pl', por:'pt', jpn:'ja', ben:'bn', hun:'hu', tel:'te',
  };
  const language = normalized[code.toLowerCase()] ?? code.toLowerCase();
  if (language === 'multi') return multipleLanguages;
  try { return new Intl.DisplayNames([localeTag], { type: 'language' }).of(language) ?? code.toUpperCase(); }
  catch { return code.toUpperCase(); }
}
